import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Car, MessageSquare, LogOut, Clock, CheckCircle2, Trash2, CalendarX, CalendarClock, Star, Volume2, VolumeX, Megaphone, Bell, BellOff, Tag, CreditCard, Download, Printer, Plus, MapPin, XCircle, ShieldAlert, PackageSearch } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toPng } from "html-to-image";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme, ThemeToggle } from "../components/ThemeToggle";
import { TORONTO_LOCATIONS } from "../data";

const CARD_SITE = "https://namarcklimo.ca";

const BOOKING_STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const QUOTE_STATUSES = ["new", "contacted", "quoted", "closed"];

const statusColor = {
  pending: "text-amber-400 border-amber-400/30",
  confirmed: "text-emerald-400 border-emerald-400/30",
  completed: "text-sky-400 border-sky-400/30",
  cancelled: "text-red-400 border-red-400/30",
  new: "text-gold border-gold",
  contacted: "text-sky-400 border-sky-400/30",
  quoted: "text-emerald-400 border-emerald-400/30",
  closed: "text-white/40 border-white/20",
};

const payColor = {
  pending: "text-white/40 border-white/20",
  authorized: "text-amber-400 border-amber-400/40",
  captured: "text-emerald-400 border-emerald-400/40",
  released: "text-red-400 border-red-400/40",
};

const Stat = ({ label, value }) => (
  <div className="border border-white/10 bg-[var(--nl-bg-2)] p-6" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
    <p className="label text-white/40 mb-3">{label}</p>
    <p className="font-serif text-5xl text-gold">{value}</p>
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [theme, toggleTheme] = useTheme("night");
  const [tab, setTab] = useState("dispatch");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [blackouts, setBlackouts] = useState([]);
  const [blackForm, setBlackForm] = useState({ date: "", all_day: true, start_time: "", end_time: "", reason: "" });
  const [driverModal, setDriverModal] = useState(null);
  const [notifyModal, setNotifyModal] = useState(null);
  const [notifyText, setNotifyText] = useState("");
  const [driverForm, setDriverForm] = useState({ driver_name: "", driver_phone: "", car_model: "", plate: "", driver_photo: "" });
  const [etas, setEtas] = useState({});
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("nl_dispatch_sound") !== "off");
  const [reviewDelay, setReviewDelay] = useState("");
  const [reviewReminder, setReviewReminder] = useState("");
  const [messages, setMessages] = useState([]);
  const [msgSearch, setMsgSearch] = useState("");
  const [msgKind, setMsgKind] = useState("all");
  const [promos, setPromos] = useState([]);
  const [promoForm, setPromoForm] = useState({ code: "", discount_type: "percent", value: "" });
  const [cardPromo, setCardPromo] = useState("");
  const cardFrontRef = useRef(null);
  const cardBackRef = useRef(null);
  const prevTodayCount = useRef(null);
  const [incidents, setIncidents] = useState([]);
  const prevIncidentCount = useRef(null);

  const load = useCallback(async () => {
    try {
      const [s, b, q, bl, st, inc] = await Promise.all([
        api.get("/stats"),
        api.get("/bookings"),
        api.get("/quotes"),
        api.get("/blackouts"),
        api.get("/settings"),
        api.get("/incidents"),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setQuotes(q.data);
      setBlackouts(bl.data);
      setReviewDelay(String(st.data.review_delay_hours));
      setReviewReminder(String(st.data.review_reminder_days));
      setIncidents(inc.data);
    } catch {
      toast.error("Failed to load data");
    }
  }, []);

  const saveReviewDelay = async () => {
    const v = parseFloat(reviewDelay);
    const r = parseFloat(reviewReminder);
    if (Number.isNaN(v) || v < 0 || v > 168) { toast.error("Review delay must be 0–168 hours"); return; }
    if (Number.isNaN(r) || r < 0 || r > 30) { toast.error("Reminder must be 0–30 days"); return; }
    try {
      await api.put("/settings", { review_delay_hours: v, review_reminder_days: r });
      toast.success(`Review text after ${v}h · reminder after ${r} day(s)`);
    } catch {
      toast.error("Could not save");
    }
  };

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab === "messages") {
      api.get("/messages").then((r) => setMessages(r.data)).catch(() => {});
    }
    if (tab === "promos" || tab === "card") {
      api.get("/promos").then((r) => setPromos(r.data)).catch(() => {});
    }
  }, [tab]);

  const createPromo = async () => {
    const value = parseFloat(promoForm.value);
    if (!promoForm.code.trim()) { toast.error("Enter a code"); return; }
    if (Number.isNaN(value) || value <= 0) { toast.error("Enter a discount amount"); return; }
    if (promoForm.discount_type === "percent" && value > 100) { toast.error("Percent can't exceed 100"); return; }
    try {
      await api.post("/promos", { code: promoForm.code.trim(), discount_type: promoForm.discount_type, value });
      toast.success("Promo code created");
      setPromoForm({ code: "", discount_type: "percent", value: "" });
      const r = await api.get("/promos"); setPromos(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create code");
    }
  };
  const deletePromo = async (id) => {
    await api.delete(`/promos/${id}`);
    setPromos((p) => p.filter((x) => x.id !== id));
    toast.success("Promo code deleted");
  };
  const downloadCard = async (ref, name) => {
    if (!ref.current) return;
    try {
      const url = await toPng(ref.current, { pixelRatio: 3, cacheBust: true });
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
    } catch { toast.error("Could not export card"); }
  };

  const [staffReasons, setStaffReasons] = useState([]);
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  useEffect(() => { api.get("/cancellation-reasons").then((r) => setStaffReasons(r.data.staff || [])).catch(() => {}); }, []);

  const openCancel = (b) => { setCancelReason(""); setCancelModal(b); };
  const doAdminCancel = async () => {
    if (!cancelReason) { toast.error("Pick a reason"); return; }
    try {
      const { data } = await api.post(`/bookings/${cancelModal.id}/cancel-admin`, { reason: cancelReason });
      toast.success(`Ride cancelled. Fee: ${data.cancellation_fee_pct}% ($${(data.cancellation_fee || 0).toFixed(2)})`);
      setCancelModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };
  const openLiveMap = (b) => window.open(`/track?session_id=${b.session_id}`, "_blank");

  // Auto-refresh so live driver locations / ETAs stay current.
  useEffect(() => {
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  // Live ETA from each en-route chauffeur to their pickup (OSRM public, no key).
  useEffect(() => {
    const onway = bookings.filter((b) => b.trip_status === "on_the_way" && b.driver_lat != null && b.driver_lng != null);
    onway.forEach(async (b) => {
      const p = TORONTO_LOCATIONS.find((l) => l.name === b.pickup);
      if (!p) return;
      try {
        const r = await fetch(`https://router.project-osrm.org/route/v1/driving/${b.driver_lng},${b.driver_lat};${p.lng},${p.lat}?overview=false`);
        const j = await r.json();
        const route = j.routes && j.routes[0];
        if (route) setEtas((prev) => ({ ...prev, [b.id]: Math.max(1, Math.round(route.duration / 60)) }));
      } catch { /* ignore */ }
    });
  }, [bookings]);

  const q = search.trim().toLowerCase();
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Toronto" });
  const todaysRides = bookings
    .filter((b) => b.date === today && b.status !== "cancelled")
    .sort((a, b) => (a.time || "").localeCompare(b.time || ""));
  const filteredBookings = bookings.filter((b) => {
    if (statusFilter !== "all" && b.status !== statusFilter) return false;
    if (dateFilter && b.date !== dateFilter) return false;
    if (q && !(`${b.name} ${b.email} ${b.phone} ${b.pickup} ${b.dropoff}`.toLowerCase().includes(q))) return false;
    return true;
  });
  const mq = msgSearch.trim().toLowerCase();
  const filteredMessages = messages.filter((m) => {
    if (msgKind !== "all" && m.kind !== msgKind) return false;
    if (mq && !(`${m.to} ${m.body} ${m.status}`.toLowerCase().includes(mq))) return false;
    return true;
  });

  // Soft chime when a new ride appears on today's board.
  const playChime = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const notes = [880, 1108.73];
      notes.forEach((f, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine"; o.frequency.value = f;
        o.connect(g); g.connect(ctx.destination);
        const t = ctx.currentTime + i * 0.18;
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.18, t + 0.03);
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
        o.start(t); o.stop(t + 0.55);
      });
      setTimeout(() => ctx.close(), 1500);
    } catch { /* ignore */ }
  }, []);

  const prevBookingCount = useRef(null);
  useEffect(() => {
    const count = bookings.length;
    if (prevBookingCount.current !== null && count > prevBookingCount.current) {
      if (soundOn) playChime();
      const latest = bookings[0];
      toast.success(`🔔 New booking${latest ? ` — ${latest.name}, ${latest.date} ${latest.time}` : ""}`, { duration: 6000 });
    }
    prevBookingCount.current = count;
  }, [bookings.length, soundOn, playChime]);

  // Alert on new SOS / lost / found reports.
  useEffect(() => {
    const count = incidents.length;
    if (prevIncidentCount.current !== null && count > prevIncidentCount.current) {
      const latest = incidents[0];
      if (soundOn) playChime();
      if (latest && latest.type === "sos") {
        toast.error(`🚨 SOS from ${latest.by === "driver" ? "driver" : "rider"} — ${latest.name || ""}`, { duration: 12000 });
      } else if (latest) {
        toast(`${latest.type === "lost" ? "Lost" : "Found"} item reported — ${latest.name || ""}`, { duration: 8000 });
      }
    }
    prevIncidentCount.current = count;
  }, [incidents, soundOn, playChime]);

  const updateIncident = async (id, body) => {
    try {
      await api.patch(`/incidents/${id}`, body);
      toast.success("Report updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const openIncidentCount = incidents.filter((i) => !["returned", "resolved"].includes(i.status)).length;

  const toggleSound = () => {
    setSoundOn((s) => {
      const next = !s;
      localStorage.setItem("nl_dispatch_sound", next ? "on" : "off");
      if (next) playChime();
      return next;
    });
  };

  const updateBooking = async (id, status) => {
    await api.patch(`/bookings/${id}/status`, { status });
    toast.success(`Booking marked ${status}`);
    load();
  };
  const notifyRider = (b) => {
    setNotifyText(`Your NamarckLimo chauffeur is outside at ${b.pickup}. Please head out when you're ready.`);
    setNotifyModal(b);
  };
  const sendNotify = async () => {
    try {
      const { data } = await api.post(`/bookings/${notifyModal.id}/notify`, { message: notifyText.trim() || undefined });
      const push = data.push_devices ? `${data.push_devices} device(s)` : "no app installs yet";
      toast.success(`Rider notified — push: ${push}, SMS: ${data.sms_status}`);
      setNotifyModal(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not notify rider");
    }
  };
  const NOTIFY_PRESETS = [
    "Your NamarckLimo chauffeur is outside — please head out when you're ready.",
    "Your chauffeur has arrived at the pickup point. Look for your vehicle.",
    "We're running about 5 minutes behind — your chauffeur is almost there. Thank you for your patience.",
  ];
  const captureBooking = async (id) => {
    try {
      await api.post(`/bookings/${id}/capture`);
      toast.success("Payment captured — card charged");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Capture failed");
    }
  };
  const releaseBooking = async (id) => {
    try {
      await api.post(`/bookings/${id}/release`);
      toast.success("Hold released — card not charged");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Release failed");
    }
  };
  const chargeBalanceBooking = async (id) => {
    try {
      const { data } = await api.post(`/bookings/${id}/charge-balance`);
      toast.success(`Balance of $${data.balance} charged`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Balance charge failed");
    }
  };
  const addBlackout = async (e) => {
    e.preventDefault();
    try {
      await api.post("/blackouts", {
        date: blackForm.date,
        all_day: blackForm.all_day,
        start_time: blackForm.all_day ? null : blackForm.start_time,
        end_time: blackForm.all_day ? null : blackForm.end_time,
        reason: blackForm.reason,
      });
      toast.success("Blackout added");
      setBlackForm({ date: "", all_day: true, start_time: "", end_time: "", reason: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add blackout");
    }
  };
  const deleteBlackout = async (id) => {
    await api.delete(`/blackouts/${id}`);
    toast.success("Blackout removed");
    load();
  };
  const openDriver = (b) => {
    setDriverForm({ driver_name: b.driver_name || "", driver_phone: b.driver_phone || "", car_model: b.car_model || "", plate: b.plate || "", driver_photo: b.driver_photo || "" });
    setDriverModal(b);
  };
  const saveDriver = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch(`/bookings/${driverModal.id}/driver`, driverForm);
      toast.success(data.sms_configured ? "Driver assigned & texted" : "Driver assigned (SMS off until Twilio number set)");
      setDriverModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not assign driver");
    }
  };
  const deleteBooking = async (id) => {
    await api.delete(`/bookings/${id}`);
    toast.success("Booking removed");
    load();
  };
  const updateQuote = async (id, status) => {
    await api.patch(`/quotes/${id}/status`, { status });
    toast.success(`Quote marked ${status}`);
    load();
  };
  const [replyModal, setReplyModal] = useState(null);
  const [replyPrice, setReplyPrice] = useState("");
  const [replyMsg, setReplyMsg] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [payLink, setPayLink] = useState(false);
  const [replyTrip, setReplyTrip] = useState({ pickup: "", dropoff: "", date: "", time: "", vehicle: "Cadillac XTS", passengers: 1, service: "Chauffeur service" });
  const openReply = (q) => {
    setReplyPrice(q.quoted_price ? String(q.quoted_price).replace(/[^0-9.]/g, "") : "");
    setReplyMsg(q.reply_message || `Hi ${q.name}, thank you for reaching out to NamarckLimo. Based on your request, here is your quote. We'd be delighted to drive you — reserve anytime below.`);
    setPayLink(false);
    setReplyTrip({ pickup: "", dropoff: "", date: "", time: "", vehicle: "Cadillac XTS", passengers: 1, service: "Chauffeur service" });
    setReplyModal(q);
  };
  const sendReply = async () => {
    if (!replyMsg.trim()) { toast.error("Please write a reply"); return; }
    if (payLink && (!replyPrice || !replyTrip.pickup || !replyTrip.dropoff || !replyTrip.date || !replyTrip.time)) {
      toast.error("For a pay link, add price, pickup, drop-off, date and time");
      return;
    }
    setReplySending(true);
    try {
      const { data } = await api.post(`/quotes/${replyModal.id}/reply`, {
        message: replyMsg.trim(),
        price: replyPrice ? parseFloat(replyPrice) : null,
        pay_link: payLink,
        ...replyTrip,
        passengers: Number(replyTrip.passengers) || 1,
      });
      toast.success(data.pay_url ? `Quote + pay link sent to ${replyModal.name}` : `Quote sent to ${replyModal.name}`);
      setReplyModal(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setReplySending(false); }
  };
  const resendLink = async (q) => {
    try {
      await api.post(`/quotes/${q.id}/resend-link`, {});
      toast.success(`Fresh 24h pay link sent to ${q.name}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not resend"); }
  };

  const doLogout = () => { logout(); navigate("/admin"); };

  return (
    <div className="min-h-screen flex bg-[var(--nl-bg)]" data-theme={theme} data-testid="admin-dashboard">
      <ThemeToggle theme={theme} onToggle={toggleTheme} />
      {/* Sidebar */}
      <aside className="w-20 md:w-64 border-r border-white/10 bg-[var(--nl-bg-2)] flex flex-col shrink-0">
        <div className="h-20 flex items-center justify-center md:justify-start md:px-8 border-b border-white/10">
          <span className="font-serif text-xl hidden md:inline">Namarck</span>
          <span className="font-serif text-xl italic text-gold hidden md:inline">Limo</span>
          <span className="font-serif text-2xl italic text-gold md:hidden">N</span>
        </div>
        <nav className="flex-1 py-8 space-y-2 px-3 md:px-4">
          <button
            onClick={() => setTab("dispatch")}
            className={`w-full flex items-center gap-3 px-3 md:px-4 py-3 label transition-colors duration-300 ${tab === "dispatch" ? "bg-gold text-black" : "text-white/60 hover:text-gold"}`}
            data-testid="tab-dispatch"
          >
            <CalendarClock size={18} /> <span className="hidden md:inline">Today</span>
          </button>
          <button
            onClick={() => setTab("bookings")}
            className={`w-full flex items-center gap-3 px-3 md:px-4 py-3 label transition-colors duration-300 ${tab === "bookings" ? "bg-gold text-black" : "text-white/60 hover:text-gold"}`}
            data-testid="tab-bookings"
          >
            <Car size={18} /> <span className="hidden md:inline">Bookings</span>
          </button>
          <button
            onClick={() => setTab("quotes")}
            className={`w-full flex items-center gap-3 px-3 md:px-4 py-3 label transition-colors duration-300 ${tab === "quotes" ? "bg-gold text-black" : "text-white/60 hover:text-gold"}`}
            data-testid="tab-quotes"
          >
            <MessageSquare size={18} /> <span className="hidden md:inline">Quotes</span>
          </button>
          <button
            onClick={() => setTab("availability")}
            className={`w-full flex items-center gap-3 px-3 md:px-4 py-3 label transition-colors duration-300 ${tab === "availability" ? "bg-gold text-black" : "text-white/60 hover:text-gold"}`}
            data-testid="tab-availability"
          >
            <CalendarX size={18} /> <span className="hidden md:inline">Availability</span>
          </button>
          <button
            onClick={() => setTab("messages")}
            className={`w-full flex items-center gap-3 px-3 md:px-4 py-3 label transition-colors duration-300 ${tab === "messages" ? "bg-gold text-black" : "text-white/60 hover:text-gold"}`}
            data-testid="tab-messages"
          >
            <MessageSquare size={18} /> <span className="hidden md:inline">Messages</span>
          </button>
          <button
            onClick={() => setTab("promos")}
            className={`w-full flex items-center gap-3 px-3 md:px-4 py-3 label transition-colors duration-300 ${tab === "promos" ? "bg-gold text-black" : "text-white/60 hover:text-gold"}`}
            data-testid="tab-promos"
          >
            <Tag size={18} /> <span className="hidden md:inline">Promo Codes</span>
          </button>
          <button
            onClick={() => setTab("card")}
            className={`w-full flex items-center gap-3 px-3 md:px-4 py-3 label transition-colors duration-300 ${tab === "card" ? "bg-gold text-black" : "text-white/60 hover:text-gold"}`}
            data-testid="tab-card"
          >
            <CreditCard size={18} /> <span className="hidden md:inline">Business Card</span>
          </button>
          <button
            onClick={() => setTab("safety")}
            className={`w-full flex items-center gap-3 px-3 md:px-4 py-3 label transition-colors duration-300 relative ${tab === "safety" ? "bg-gold text-black" : "text-white/60 hover:text-gold"}`}
            data-testid="tab-safety"
          >
            <ShieldAlert size={18} /> <span className="hidden md:inline">Safety & Lost/Found</span>
            {openIncidentCount > 0 && (
              <span className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="safety-badge">{openIncidentCount}</span>
            )}
          </button>
        </nav>
        <button onClick={doLogout} className="flex items-center gap-3 px-3 md:px-8 py-6 border-t border-white/10 text-white/50 hover:text-red-400 label transition-colors duration-300" data-testid="logout-btn">
          <LogOut size={18} /> <span className="hidden md:inline">Sign Out</span>
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-x-hidden">
        <header className="h-20 border-b border-white/10 flex items-center justify-between px-6 md:px-10">
          <h1 className="font-serif font-light text-3xl capitalize">{tab}</h1>
          <p className="text-white/40 font-body text-sm hidden sm:block">{user?.email}</p>
        </header>

        <div className="p-6 md:p-10">
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              <Stat label="Total Bookings" value={stats.total_bookings} />
              <Stat label="Pending" value={stats.pending_bookings} />
              <Stat label="Confirmed" value={stats.confirmed_bookings} />
              <Stat label="New Quotes" value={stats.new_quotes} />
            </div>
          )}

          {tab === "dispatch" ? (
            <div data-testid="dispatch-panel">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif font-light text-3xl">Today · {today}</h2>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2" data-testid="review-delay-control">
                    <span className="label text-white/40 text-[10px] hidden lg:inline">Review after</span>
                    <input
                      type="number" min="0" max="168" step="0.5"
                      value={reviewDelay}
                      onChange={(e) => setReviewDelay(e.target.value)}
                      className="w-14 bg-[var(--nl-bg-3)] border border-white/10 px-2 py-1 text-white font-body text-xs focus:outline-none focus:border-gold"
                      data-testid="review-delay-input"
                    />
                    <span className="label text-white/40 text-[10px]">hrs</span>
                    <span className="label text-white/40 text-[10px] hidden lg:inline">· nudge after</span>
                    <input
                      type="number" min="0" max="30" step="1"
                      value={reviewReminder}
                      onChange={(e) => setReviewReminder(e.target.value)}
                      className="w-14 bg-[var(--nl-bg-3)] border border-white/10 px-2 py-1 text-white font-body text-xs focus:outline-none focus:border-gold"
                      data-testid="review-reminder-input"
                    />
                    <span className="label text-white/40 text-[10px]">days</span>
                    <button onClick={saveReviewDelay} className="label text-[10px] text-gold border border-gold/40 px-2 py-1 hover:bg-gold/10 transition-colors duration-300" data-testid="review-delay-save">Save</button>
                  </div>
                  <button onClick={toggleSound} className={`flex items-center gap-1.5 label text-xs transition-colors duration-300 ${soundOn ? "text-gold" : "text-white/40 hover:text-gold"}`} data-testid="dispatch-sound-toggle" title="New-ride chime">
                    {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
                    <span className="hidden sm:inline">{soundOn ? "Chime on" : "Chime off"}</span>
                  </button>
                  <span className="label text-gold">{todaysRides.length} ride{todaysRides.length === 1 ? "" : "s"}</span>
                </div>
              </div>
              {todaysRides.length === 0 ? (
                <div className="border border-white/10 bg-[var(--nl-bg-2)] p-12 text-center" data-testid="dispatch-empty">
                  <CalendarClock size={32} className="text-gold/40 mx-auto mb-4" />
                  <p className="text-white/40 font-body">No rides scheduled for today. Enjoy the calm.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {todaysRides.map((b) => (
                    <div key={b.id} className="border border-white/10 bg-[var(--nl-bg-2)] p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`dispatch-ride-${b.id}`}>
                      <div className="md:w-24 shrink-0">
                        <p className="font-serif text-3xl text-gold">{b.time}</p>
                        <p className="text-white/40 text-xs font-body">{b.vehicle}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-body"><span className="text-gold">↑</span> {b.pickup}</p>
                        <p className="text-white/70 font-body"><span className="text-gold">↓</span> {b.dropoff}</p>
                        <p className="text-white/40 text-xs font-body mt-1">{b.name} · {b.phone} · {b.passengers} pax{b.notes ? ` · ${b.notes}` : ""}</p>
                      </div>
                      <div className="md:w-56 shrink-0 flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <span className={`border px-2 py-0.5 label text-[10px] ${b.trip_status === "completed" ? "text-emerald-400 border-emerald-400/40" : b.trip_status === "on_the_way" ? "text-sky-400 border-sky-400/40" : b.driver_name ? "text-amber-400 border-amber-400/40" : "text-white/40 border-white/20"}`}>
                            {b.trip_status ? b.trip_status.replace(/_/g, " ") : "unassigned"}
                          </span>
                          <span className={`border px-2 py-0.5 label text-[10px] ${payColor[b.payment_status] || "text-white/40 border-white/20"}`}>{b.payment_status}</span>
                          {b.trip_status === "on_the_way" && etas[b.id] != null && (
                            <span className="border border-gold text-gold px-2 py-0.5 label text-[10px] whitespace-nowrap" data-testid={`dispatch-eta-${b.id}`}>ETA ~{etas[b.id]} min</span>
                          )}
                        </div>
                        {b.driver_name ? (
                          <p className="text-white/70 font-body text-sm">🧑‍✈️ {b.driver_name} · {b.driver_phone}</p>
                        ) : null}
                        {b.trip_status === "completed" && b.rating && (
                          <div className="flex items-center gap-0.5" data-testid={`dispatch-rating-${b.id}`}>
                            {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={13} className={i <= b.rating ? "fill-gold text-gold" : "text-white/20"} />)}
                          </div>
                        )}
                        <button onClick={() => openDriver(b)} className="label text-gold border border-gold/40 px-3 py-1.5 hover:bg-gold/10 transition-colors duration-300 text-xs" data-testid={`dispatch-assign-${b.id}`}>
                          {b.driver_name ? "Reassign / Notify" : "Assign Driver"}
                        </button>
                        {b.trip_status !== "completed" && (
                          <span
                            className={`flex items-center gap-1 label text-[10px] px-2 py-1 border ${b.push_enabled ? "text-emerald-400 border-emerald-400/40" : "text-white/35 border-white/15"}`}
                            title={b.push_enabled ? "Rider installed the app — push will be delivered" : "Rider hasn't enabled app alerts; they'll still get an SMS"}
                            data-testid={`dispatch-alerts-${b.id}`}
                          >
                            {b.push_enabled ? <Bell size={11} /> : <BellOff size={11} />}
                            {b.push_enabled ? "Alerts on" : "No app"}
                          </span>
                        )}
                        {b.driver_name && b.trip_status !== "completed" && (
                          <button onClick={() => notifyRider(b)} className="flex items-center justify-center gap-1.5 label text-black bg-gold px-3 py-1.5 hover:bg-[var(--nl-gold-hover)] transition-colors duration-300 text-xs" data-testid={`dispatch-notify-${b.id}`}>
                            <Megaphone size={13} /> We're outside
                          </button>
                        )}
                        {b.trip_status !== "completed" && (
                          <div className="flex gap-2">
                            <button onClick={() => openLiveMap(b)} className="flex-1 flex items-center justify-center gap-1.5 label text-sky-400 border border-sky-400/40 px-3 py-1.5 hover:bg-sky-400/10 transition-colors duration-300 text-xs" data-testid={`dispatch-map-${b.id}`}>
                              <MapPin size={13} /> Live map
                            </button>
                            <button onClick={() => openCancel(b)} className="flex-1 flex items-center justify-center gap-1.5 label text-red-400 border border-red-400/40 px-3 py-1.5 hover:bg-red-400/10 transition-colors duration-300 text-xs" data-testid={`dispatch-cancel-${b.id}`}>
                              <XCircle size={13} /> Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : tab === "bookings" ? (
            <>
              <div className="flex flex-col md:flex-row gap-3 mb-5" data-testid="booking-filters">
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, email, phone, route…"
                  className="flex-1 bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold transition-colors duration-300"
                  data-testid="booking-search"
                />
                <input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm [color-scheme:dark] focus:outline-none focus:border-gold transition-colors duration-300"
                  data-testid="booking-date-filter"
                />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold transition-colors duration-300"
                  data-testid="booking-status-filter"
                >
                  <option value="all">All statuses</option>
                  {BOOKING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {(search || dateFilter || statusFilter !== "all") && (
                  <button
                    onClick={() => { setSearch(""); setDateFilter(""); setStatusFilter("all"); }}
                    className="label text-white/50 border border-white/15 px-4 hover:text-gold hover:border-gold transition-colors duration-300"
                    data-testid="clear-filters"
                  >
                    Clear
                  </button>
                )}
              </div>
            <div className="border border-white/10 bg-[var(--nl-bg-2)] overflow-x-auto" data-testid="bookings-table">
              {bookings.length === 0 ? (
                <p className="p-10 text-white/40 font-body">No bookings yet.</p>
              ) : filteredBookings.length === 0 ? (
                <p className="p-10 text-white/40 font-body" data-testid="no-results">No bookings match your filters.</p>
              ) : (
                <table className="w-full text-left font-body text-sm min-w-[900px]">
                  <thead>
                    <tr className="label text-white/40 border-b border-white/10">
                      <th className="px-5 py-4">Client</th>
                      <th className="px-5 py-4">Service</th>
                      <th className="px-5 py-4">Route</th>
                      <th className="px-5 py-4">When</th>
                      <th className="px-5 py-4">Vehicle</th>
                      <th className="px-5 py-4">Driver</th>
                      <th className="px-5 py-4">Payment</th>
                      <th className="px-5 py-4">Status</th>
                      <th className="px-5 py-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings.map((b) => (
                      <tr key={b.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`booking-row-${b.id}`}>
                        <td className="px-5 py-4">
                          <p className="text-white">{b.name}</p>
                          <p className="text-white/40 text-xs">{b.email}</p>
                          <p className="text-white/40 text-xs">{b.phone}</p>
                        </td>
                        <td className="px-5 py-4 text-white/70">{b.service}<br /><span className="text-white/40 text-xs">{b.passengers} pax</span></td>
                        <td className="px-5 py-4 text-white/70 max-w-[200px]">
                          <span className="text-gold">↑</span> {b.pickup}<br />
                          <span className="text-gold">↓</span> {b.dropoff}
                        </td>
                        <td className="px-5 py-4 text-white/70">{b.date}<br /><span className="text-white/40">{b.time}</span></td>
                        <td className="px-5 py-4 text-white/70">{b.vehicle}</td>
                        <td className="px-5 py-4">
                          {b.driver_name ? (
                            <div>
                              <p className="text-white/80">{b.driver_name}</p>
                              <p className="text-white/40 text-xs">{b.driver_phone}</p>
                              {b.trip_status && (
                                <span className={`inline-block mt-1 border px-1.5 py-0.5 label text-[9px] ${b.trip_status === "completed" ? "text-emerald-400 border-emerald-400/40" : b.trip_status === "on_the_way" ? "text-sky-400 border-sky-400/40" : "text-amber-400 border-amber-400/40"}`} data-testid={`trip-status-${b.id}`}>
                                  {b.trip_status.replace(/_/g, " ")}
                                </span>
                              )}
                              <button onClick={() => openDriver(b)} className="label text-gold text-[10px] mt-1 block hover:underline" data-testid={`edit-driver-${b.id}`}>Edit / re-notify</button>
                              {b.rating && (
                                <div className="flex items-center gap-0.5 mt-1" data-testid={`booking-rating-${b.id}`}>
                                  {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={11} className={i <= b.rating ? "fill-gold text-gold" : "text-white/20"} />)}
                                </div>
                              )}
                            </div>
                          ) : (
                            <button onClick={() => openDriver(b)} className="label text-gold border border-gold/40 px-2 py-1 hover:bg-gold/10 transition-colors duration-300 whitespace-nowrap" data-testid={`assign-driver-${b.id}`}>Assign</button>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-white/80">{b.amount ? `$${b.amount}` : "—"}</p>
                          <span className={`inline-block mt-1 border px-2 py-0.5 label ${payColor[b.payment_status] || "text-white/40 border-white/20"}`}>
                            {b.payment_status || "pending"}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <select
                            value={b.status}
                            onChange={(e) => updateBooking(b.id, e.target.value)}
                            className={`bg-transparent border px-2 py-1 label ${statusColor[b.status] || "text-white/60 border-white/20"}`}
                            data-testid={`booking-status-${b.id}`}
                          >
                            {BOOKING_STATUSES.map((s) => <option key={s} value={s} className="bg-[var(--nl-bg-3)] text-white">{s}</option>)}
                          </select>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            {b.payment_status === "authorized" && (
                              <>
                                <button onClick={() => captureBooking(b.id)} className="label text-emerald-400 border border-emerald-400/40 px-2 py-1 hover:bg-emerald-400/10 transition-colors duration-300" data-testid={`capture-${b.id}`}>Charge</button>
                                <button onClick={() => releaseBooking(b.id)} className="label text-amber-400 border border-amber-400/40 px-2 py-1 hover:bg-amber-400/10 transition-colors duration-300" data-testid={`release-${b.id}`}>Release</button>
                              </>
                            )}
                            {b.is_deposit && !b.balance_charged && b.payment_method_id && (b.payment_status === "captured" || b.payment_status === "authorized") && (
                              <button onClick={() => chargeBalanceBooking(b.id)} className="label text-gold border border-gold/50 px-2 py-1 hover:bg-gold/10 transition-colors duration-300 whitespace-nowrap" data-testid={`charge-balance-${b.id}`}>Charge Balance</button>
                            )}
                            <button onClick={() => deleteBooking(b.id)} className="text-white/40 hover:text-red-400 transition-colors duration-300" data-testid={`delete-booking-${b.id}`}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            </>
          ) : tab === "quotes" ? (
            <div className="border border-white/10 bg-[var(--nl-bg-2)] overflow-x-auto" data-testid="quotes-table">
              {quotes.length === 0 ? (
                <p className="p-10 text-white/40 font-body">No quote requests yet.</p>
              ) : (
                <table className="w-full text-left font-body text-sm min-w-[700px]">
                  <thead>
                    <tr className="label text-white/40 border-b border-white/10">
                      <th className="px-5 py-4">Client</th>
                      <th className="px-5 py-4">Message</th>
                      <th className="px-5 py-4">Status</th>
                      <th className="px-5 py-4">Reply</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.map((q) => (
                      <tr key={q.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`quote-row-${q.id}`}>
                        <td className="px-5 py-4">
                          <p className="text-white">{q.name}</p>
                          <p className="text-white/40 text-xs">{q.email}</p>
                          <p className="text-white/40 text-xs">{q.phone}</p>
                        </td>
                        <td className="px-5 py-4 text-white/70 max-w-md">
                          {q.message}
                          {q.reply_message && (
                            <div className="mt-2 border-l-2 border-gold/50 pl-3 text-xs" data-testid={`quote-sent-${q.id}`}>
                              <span className="label text-gold/70 text-[10px]">Sent{q.quoted_price ? ` · ${q.quoted_price}` : ""}</span>
                              <p className="text-white/50 mt-0.5">{q.reply_message}</p>
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <select
                            value={q.status}
                            onChange={(e) => updateQuote(q.id, e.target.value)}
                            className={`bg-transparent border px-2 py-1 label ${statusColor[q.status] || "text-white/60 border-white/20"}`}
                            data-testid={`quote-status-${q.id}`}
                          >
                            {QUOTE_STATUSES.map((s) => <option key={s} value={s} className="bg-[var(--nl-bg-3)] text-white">{s}</option>)}
                          </select>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-col gap-2">
                            <button onClick={() => openReply(q)} className="label text-xs text-black bg-gold px-3 py-1.5 hover:bg-[var(--nl-gold-hover)] transition-colors duration-300 whitespace-nowrap" data-testid={`quote-reply-${q.id}`}>
                              {q.reply_message ? "Re-send" : "Reply"}
                            </button>
                            {q.pay_offer && (
                              <button onClick={() => resendLink(q)} className="label text-xs text-gold border border-gold/40 px-3 py-1.5 hover:bg-gold/10 transition-colors duration-300 whitespace-nowrap" data-testid={`quote-resend-${q.id}`}>
                                Fresh 24h link
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : tab === "messages" ? (
            <>
              <div className="flex flex-col md:flex-row gap-3 mb-5" data-testid="message-filters">
                <input
                  value={msgSearch}
                  onChange={(e) => setMsgSearch(e.target.value)}
                  placeholder="Search recipient or message text…"
                  className="flex-1 bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold transition-colors duration-300"
                  data-testid="message-search"
                />
                <select
                  value={msgKind}
                  onChange={(e) => setMsgKind(e.target.value)}
                  className="bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold transition-colors duration-300"
                  data-testid="message-kind-filter"
                >
                  <option value="all">All types</option>
                  {[...new Set(messages.map((m) => m.kind).filter(Boolean))].map((k) => (
                    <option key={k} value={k}>{k.replace(/_/g, " ")}</option>
                  ))}
                </select>
                {(msgSearch || msgKind !== "all") && (
                  <button
                    onClick={() => { setMsgSearch(""); setMsgKind("all"); }}
                    className="label text-white/50 border border-white/15 px-4 hover:text-gold hover:border-gold transition-colors duration-300"
                    data-testid="message-clear-filters"
                  >
                    Clear
                  </button>
                )}
              </div>
            <div className="border border-white/10 bg-[var(--nl-bg-2)] overflow-x-auto" data-testid="messages-table">
              {filteredMessages.length === 0 ? (
                <p className="p-10 text-white/40 font-body">{messages.length === 0 ? "No messages sent yet. Texts will appear here as bookings, reminders, and alerts go out." : "No messages match your filters."}</p>
              ) : (
                <table className="w-full text-left font-body text-sm min-w-[720px]">
                  <thead>
                    <tr className="label text-white/40 border-b border-white/10">
                      <th className="px-5 py-4">When</th>
                      <th className="px-5 py-4">To</th>
                      <th className="px-5 py-4">Type</th>
                      <th className="px-5 py-4">Message</th>
                      <th className="px-5 py-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMessages.map((m) => (
                      <tr key={m.id} className="border-b border-white/5 hover:bg-white/[0.02] align-top" data-testid={`message-row-${m.id}`}>
                        <td className="px-5 py-4 text-white/50 whitespace-nowrap">{new Date(m.created_at).toLocaleString()}</td>
                        <td className="px-5 py-4 text-white/80 whitespace-nowrap">{m.to}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-1.5">
                            <span className={`label text-[10px] border px-2 py-0.5 ${m.channel === "push" ? "text-sky-400 border-sky-400/40" : "text-gold border-gold/30"}`}>{m.channel === "push" ? "PUSH" : "SMS"}</span>
                            <span className="label text-[10px] text-white/50 border border-white/15 px-2 py-0.5">{(m.kind || "").replace(/_/g, " ")}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-white/70 max-w-md">{m.body}</td>
                        <td className="px-5 py-4">
                          <span className={`label text-[10px] border px-2 py-0.5 whitespace-nowrap ${["delivered", "sent", "queued", "accepted"].includes(m.status) ? "text-emerald-400 border-emerald-400/40" : m.status === "failed" ? "text-red-400 border-red-400/40" : "text-white/50 border-white/20"}`}>
                            {m.status}
                          </span>
                          {m.error && <p className="text-red-400/70 text-[10px] mt-1 max-w-[180px]">{m.error}</p>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            </>
          ) : tab === "promos" ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="promos-panel">
              <div className="border border-white/10 bg-[var(--nl-bg-2)] p-6 space-y-4 h-fit">
                <p className="label text-gold">Create a promo code</p>
                <div>
                  <label className="label text-white/50 block mb-2">Code</label>
                  <input value={promoForm.code} onChange={(e) => setPromoForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="CARD10" className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="promo-code-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label text-white/50 block mb-2">Discount type</label>
                    <select value={promoForm.discount_type} onChange={(e) => setPromoForm((f) => ({ ...f, discount_type: e.target.value }))} className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="promo-type-select">
                      <option value="percent">Percentage (%)</option>
                      <option value="fixed">Fixed amount ($)</option>
                    </select>
                  </div>
                  <div>
                    <label className="label text-white/50 block mb-2">{promoForm.discount_type === "percent" ? "Percent off" : "Dollars off"}</label>
                    <input type="number" min="1" value={promoForm.value} onChange={(e) => setPromoForm((f) => ({ ...f, value: e.target.value }))} placeholder={promoForm.discount_type === "percent" ? "10" : "25"} className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="promo-value-input" />
                  </div>
                </div>
                <button onClick={createPromo} className="w-full flex items-center justify-center gap-2 label bg-gold text-black py-3 hover:bg-[var(--nl-gold-hover)] transition-colors duration-300" data-testid="promo-create-btn"><Plus size={15} /> Create Code</button>
                <p className="text-white/35 text-xs font-body">You decide the discount. The client enters the code at booking and it comes off their final total.</p>
              </div>
              <div className="border border-white/10 bg-[var(--nl-bg-2)] p-6" data-testid="promo-list">
                <p className="label text-gold mb-4">Active codes</p>
                {promos.length === 0 ? (
                  <p className="text-white/40 font-body text-sm">No promo codes yet. Create one on the left.</p>
                ) : (
                  <ul className="divide-y divide-white/5">
                    {promos.map((p) => (
                      <li key={p.id} className="flex items-center justify-between py-3" data-testid={`promo-row-${p.id}`}>
                        <div>
                          <p className="font-serif text-lg text-white">{p.code}</p>
                          <p className="text-white/45 text-xs font-body">{p.discount_type === "percent" ? `${p.value}% off` : `$${p.value} off`} the final total</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <span className="label text-[10px] text-gold border border-gold/30 px-2.5 py-1 whitespace-nowrap block" data-testid={`promo-uses-${p.id}`}>
                              used {p.uses || 0}×
                            </span>
                            <span className="text-white/40 text-[10px] font-body mt-1 block" data-testid={`promo-discount-total-${p.id}`}>
                              ${(p.total_discount || 0).toFixed(2)} given
                            </span>
                          </div>
                          <button onClick={() => deletePromo(p.id)} className="text-white/40 hover:text-red-400 transition-colors duration-300" data-testid={`promo-delete-${p.id}`}><Trash2 size={16} /></button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : tab === "card" ? (
            <div data-testid="card-panel">
              <div className="flex flex-wrap items-center gap-4 mb-8">
                <div>
                  <label className="label text-white/50 block mb-2">Promo code on the card</label>
                  <select value={cardPromo} onChange={(e) => setCardPromo(e.target.value)} className="bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="card-promo-select">
                    <option value="">No code — just the website</option>
                    {promos.map((p) => <option key={p.id} value={p.code}>{p.code} ({p.discount_type === "percent" ? `${p.value}%` : `$${p.value}`} off)</option>)}
                  </select>
                </div>
                <div className="flex gap-3 md:ml-auto">
                  <button onClick={() => downloadCard(cardFrontRef, "namarcklimo-card-front.png")} className="flex items-center gap-2 label border border-gold/40 text-gold px-4 py-2.5 hover:bg-gold/10 transition-colors duration-300" data-testid="card-download-front"><Download size={15} /> Front</button>
                  <button onClick={() => downloadCard(cardBackRef, "namarcklimo-card-back.png")} className="flex items-center gap-2 label border border-gold/40 text-gold px-4 py-2.5 hover:bg-gold/10 transition-colors duration-300" data-testid="card-download-back"><Download size={15} /> Back</button>
                  <button onClick={() => window.print()} className="flex items-center gap-2 label bg-gold text-black px-4 py-2.5 hover:bg-[var(--nl-gold-hover)] transition-colors duration-300" data-testid="card-print"><Printer size={15} /> Print</button>
                </div>
              </div>

              <div className="nl-printable flex flex-wrap gap-8">
                {/* FRONT */}
                <div ref={cardFrontRef} className="relative w-[420px] h-[240px] bg-[#050505] overflow-hidden" data-testid="card-front" style={{ fontFamily: "Georgia, serif" }}>
                  <div className="absolute inset-0" style={{ border: "1px solid rgba(212,175,55,0.4)", margin: "10px" }} />
                  <div className="relative h-full flex flex-col justify-between px-8 py-7">
                    <div>
                      <p style={{ color: "#F5F5F0", fontSize: "24px", letterSpacing: "0.5px" }}>Namarck<span style={{ color: "#D4AF37", fontStyle: "italic" }}>Limo</span></p>
                      <p style={{ color: "#D4AF37", fontSize: "8px", letterSpacing: "3px", marginTop: "5px", textTransform: "uppercase", fontFamily: "Arial, sans-serif" }}>Private Chauffeur Service · Across Ontario</p>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                      <div>
                        <p style={{ color: "#F5F5F0", fontSize: "20px" }}>Michael Manu</p>
                        <p style={{ color: "#D4AF37", fontSize: "9px", letterSpacing: "2px", marginTop: "2px", textTransform: "uppercase", fontFamily: "Arial, sans-serif" }}>Chief Executive Officer</p>
                      </div>
                      <div style={{ textAlign: "right", fontFamily: "Arial, sans-serif" }}>
                        <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "11px" }}>+1 289 928 2427</p>
                        <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "11px", marginTop: "3px" }}>admin@namarcklimo.ca</p>
                        <p style={{ color: "#D4AF37", fontSize: "11px", marginTop: "3px" }}>namarcklimo.ca</p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* BACK */}
                <div ref={cardBackRef} className="relative w-[420px] h-[240px] bg-[#050505] overflow-hidden flex items-center gap-6 px-8" data-testid="card-back" style={{ fontFamily: "Arial, sans-serif" }}>
                  <div className="absolute inset-0" style={{ border: "1px solid rgba(212,175,55,0.4)", margin: "10px" }} />
                  <div className="relative bg-white p-2.5 rounded-sm">
                    <QRCodeCanvas value={cardPromo ? `${CARD_SITE}/?promo=${cardPromo}` : CARD_SITE} size={128} level="M" includeMargin={false} />
                  </div>
                  <div className="relative">
                    <p style={{ color: "#D4AF37", fontSize: "10px", letterSpacing: "2px", textTransform: "uppercase" }}>Scan to book</p>
                    <p style={{ color: "#F5F5F0", fontSize: "18px", fontFamily: "Georgia, serif", marginTop: "6px", lineHeight: 1.2 }}>Your ride, one scan away</p>
                    {cardPromo && (
                      <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "12px", marginTop: "10px" }}>
                        Use code <span style={{ color: "#D4AF37" }}>{cardPromo}</span> for your discount — applied automatically.
                      </p>
                    )}
                    <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "11px", marginTop: "10px" }}>namarcklimo.ca</p>
                  </div>
                </div>
              </div>
              <p className="text-white/35 text-xs font-body mt-6 max-w-xl">Standard business-card size (3.5" × 2"). Download the front and back as high-resolution PNGs and send them to any printer, or hit Print. The QR opens your site{cardPromo ? ` and auto-applies ${cardPromo}` : ""}.</p>
            </div>
          ) : tab === "safety" ? (
            <div data-testid="safety-panel" className="space-y-8">
              {["sos", "lost", "found"].map((type) => {
                const list = incidents.filter((i) => i.type === type);
                const meta = {
                  sos: { title: "Emergency / SOS alerts", Icon: ShieldAlert, tone: "text-red-400 border-red-400/40" },
                  lost: { title: "Lost items (reported by riders)", Icon: PackageSearch, tone: "text-amber-400 border-amber-400/40" },
                  found: { title: "Found items (reported by drivers)", Icon: PackageSearch, tone: "text-emerald-400 border-emerald-400/40" },
                }[type];
                const foundOptions = incidents.filter((i) => i.type === "found");
                return (
                  <div key={type} data-testid={`safety-section-${type}`}>
                    <div className="flex items-center gap-2 mb-4">
                      <meta.Icon size={18} className={meta.tone.split(" ")[0]} />
                      <h3 className="font-serif font-light text-2xl">{meta.title}</h3>
                      <span className="label text-white/40 text-xs">{list.length}</span>
                    </div>
                    {list.length === 0 ? (
                      <p className="text-white/35 font-body text-sm border border-white/10 bg-[var(--nl-bg-2)] p-6">Nothing reported.</p>
                    ) : (
                      <div className="space-y-3">
                        {list.map((i) => (
                          <div key={i.id} className={`border ${meta.tone} bg-[var(--nl-bg-2)] p-5`} data-testid={`incident-${i.id}`}>
                            <div className="flex flex-col md:flex-row md:items-start gap-4">
                              <div className="flex-1 min-w-0">
                                {type === "sos" ? (
                                  <p className="text-white font-body font-semibold">🚨 SOS from {i.by === "driver" ? "chauffeur" : "rider"}</p>
                                ) : (
                                  <p className="text-white font-body">"{i.description}"</p>
                                )}
                                <p className="text-white/50 text-sm font-body mt-1">{i.name} · {i.phone || i.contact_phone || "no phone"}</p>
                                <p className="text-white/40 text-xs font-body mt-0.5">{i.date} {i.time} · {i.pickup} → {i.dropoff}</p>
                                {i.driver_name && <p className="text-white/40 text-xs font-body">Chauffeur: {i.driver_name} · {i.driver_phone}</p>}
                                {i.note && <p className="text-white/50 text-xs font-body mt-1 italic">{i.note}</p>}
                                {type === "sos" && i.lat != null && (
                                  <a href={`https://maps.google.com/?q=${i.lat},${i.lng}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 label text-sky-400 text-xs mt-2 hover:underline" data-testid={`incident-map-${i.id}`}>
                                    <MapPin size={12} /> View location
                                  </a>
                                )}
                              </div>
                              <div className="md:w-64 shrink-0 space-y-2">
                                <div className="flex items-center gap-2">
                                  <span className="label text-white/40 text-[10px]">Status</span>
                                  <span className={`label text-[10px] px-2 py-0.5 border ${i.status === "returned" || i.status === "resolved" ? "text-emerald-400 border-emerald-400/40" : "text-amber-400 border-amber-400/40"}`}>{i.status}</span>
                                </div>
                                {type === "sos" && i.status !== "resolved" && (
                                  <button onClick={() => updateIncident(i.id, { status: "resolved" })} className="w-full label text-xs border border-emerald-400/40 text-emerald-400 py-2 hover:bg-emerald-400/10 transition-colors duration-300" data-testid={`incident-resolve-${i.id}`}>Mark resolved</button>
                                )}
                                {type === "lost" && i.status !== "returned" && (
                                  <>
                                    <select
                                      value={i.matched_with || ""}
                                      onChange={(e) => updateIncident(i.id, { matched_with: e.target.value, status: "matched" })}
                                      className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-2 py-1.5 text-white font-body text-xs focus:outline-none focus:border-gold"
                                      data-testid={`incident-match-${i.id}`}
                                    >
                                      <option value="">Match to a found item…</option>
                                      {foundOptions.map((f) => <option key={f.id} value={f.id}>{f.description} ({f.date})</option>)}
                                    </select>
                                    <div className="flex gap-2">
                                      <button onClick={() => updateIncident(i.id, { status: "found" })} className="flex-1 label text-xs border border-sky-400/40 text-sky-400 py-2 hover:bg-sky-400/10 transition-colors duration-300" data-testid={`incident-found-${i.id}`}>Mark found</button>
                                      <button onClick={() => updateIncident(i.id, { status: "returned" })} className="flex-1 label text-xs bg-gold text-black py-2 hover:bg-[var(--nl-gold-hover)] transition-colors duration-300" data-testid={`incident-returned-${i.id}`}>Returned</button>
                                    </div>
                                    <p className="text-white/30 text-[10px] font-body">Rider is texted &amp; push-notified on Found and Returned.</p>
                                  </>
                                )}
                                {type === "found" && i.status !== "returned" && (
                                  <button onClick={() => updateIncident(i.id, { status: "returned" })} className="w-full label text-xs bg-gold text-black py-2 hover:bg-[var(--nl-gold-hover)] transition-colors duration-300" data-testid={`incident-returned-${i.id}`}>Mark returned</button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="availability-panel">
              <form onSubmit={addBlackout} className="border border-white/10 bg-[var(--nl-bg-2)] p-6 space-y-4 h-fit" data-testid="blackout-form">
                <h3 className="font-serif font-light text-2xl">Block a date/time</h3>
                <p className="text-white/40 text-sm font-body">Blocked slots can't be booked by customers.</p>
                <div>
                  <label className="label text-white/50 block mb-2">Date</label>
                  <input required type="date" value={blackForm.date} onChange={(e) => setBlackForm((f) => ({ ...f, date: e.target.value }))} className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm [color-scheme:dark] focus:outline-none focus:border-gold" data-testid="blackout-date" />
                </div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={blackForm.all_day} onChange={(e) => setBlackForm((f) => ({ ...f, all_day: e.target.checked }))} className="w-4 h-4 accent-[var(--nl-gold)]" data-testid="blackout-allday" />
                  <span className="text-white/70 font-body text-sm">Block the entire day</span>
                </label>
                {!blackForm.all_day && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label text-white/50 block mb-2">From</label>
                      <input type="time" value={blackForm.start_time} onChange={(e) => setBlackForm((f) => ({ ...f, start_time: e.target.value }))} className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-3 py-2.5 text-white font-body text-sm [color-scheme:dark] focus:outline-none focus:border-gold" data-testid="blackout-start" />
                    </div>
                    <div>
                      <label className="label text-white/50 block mb-2">To</label>
                      <input type="time" value={blackForm.end_time} onChange={(e) => setBlackForm((f) => ({ ...f, end_time: e.target.value }))} className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-3 py-2.5 text-white font-body text-sm [color-scheme:dark] focus:outline-none focus:border-gold" data-testid="blackout-end" />
                    </div>
                  </div>
                )}
                <div>
                  <label className="label text-white/50 block mb-2">Reason (optional)</label>
                  <input value={blackForm.reason} onChange={(e) => setBlackForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Fully booked, maintenance…" className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="blackout-reason" />
                </div>
                <button type="submit" className="w-full label bg-gold text-black py-3 hover:bg-[var(--nl-gold-hover)] transition-colors duration-300" data-testid="blackout-submit">Add Blackout</button>
              </form>

              <div className="border border-white/10 bg-[var(--nl-bg-2)] p-6" data-testid="blackout-list">
                <h3 className="font-serif font-light text-2xl mb-4">Blocked slots</h3>
                {blackouts.length === 0 ? (
                  <p className="text-white/40 font-body text-sm">No blocked dates yet.</p>
                ) : (
                  <ul className="divide-y divide-white/5">
                    {blackouts.map((bl) => (
                      <li key={bl.id} className="flex items-center justify-between py-3" data-testid={`blackout-row-${bl.id}`}>
                        <div>
                          <p className="text-white font-body">{bl.date} {bl.all_day ? "· All day" : `· ${bl.start_time}–${bl.end_time}`}</p>
                          {bl.reason && <p className="text-white/40 text-xs font-body">{bl.reason}</p>}
                        </div>
                        <button onClick={() => deleteBlackout(bl.id)} className="text-white/40 hover:text-red-400 transition-colors duration-300" data-testid={`delete-blackout-${bl.id}`}>
                          <Trash2 size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {driverModal && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDriverModal(null)} data-testid="driver-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={saveDriver} className="w-full max-w-md border border-white/10 bg-[var(--nl-bg-2)] p-8 space-y-5">
            <div>
              <p className="label text-gold mb-1">Assign Chauffeur</p>
              <h3 className="font-serif font-light text-2xl">{driverModal.date} · {driverModal.time}</h3>
              <p className="text-white/40 text-sm font-body mt-1">{driverModal.vehicle} — {driverModal.pickup} → {driverModal.dropoff}</p>
            </div>
            <div>
              <label className="label text-white/50 block mb-2">Driver Name</label>
              <input required value={driverForm.driver_name} onChange={(e) => setDriverForm((f) => ({ ...f, driver_name: e.target.value }))} className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="driver-name" />
            </div>
            <div>
              <label className="label text-white/50 block mb-2">Driver Phone</label>
              <input required value={driverForm.driver_phone} onChange={(e) => setDriverForm((f) => ({ ...f, driver_phone: e.target.value }))} placeholder="+1 416…" className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="driver-phone" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label text-white/50 block mb-2">Car Model</label>
                <input value={driverForm.car_model} onChange={(e) => setDriverForm((f) => ({ ...f, car_model: e.target.value }))} placeholder="Cadillac Escalade" className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="driver-car-model" />
              </div>
              <div>
                <label className="label text-white/50 block mb-2">Plate</label>
                <input value={driverForm.plate} onChange={(e) => setDriverForm((f) => ({ ...f, plate: e.target.value }))} placeholder="ABCD 123" className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="driver-plate" />
              </div>
            </div>
            <div>
              <label className="label text-white/50 block mb-2">Chauffeur Photo URL (optional)</label>
              <input value={driverForm.driver_photo} onChange={(e) => setDriverForm((f) => ({ ...f, driver_photo: e.target.value }))} placeholder="https://…" className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="driver-photo" />
            </div>
            <p className="text-white/35 text-xs font-body">The driver gets a text with the full trip details (client, route, time, notes). Car, plate & photo appear on the client's tracking page.</p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setDriverModal(null)} className="flex-1 label border border-white/15 text-white/60 py-3 hover:text-white transition-colors duration-300" data-testid="driver-cancel">Cancel</button>
              <button type="submit" className="flex-1 label bg-gold text-black py-3 hover:bg-[var(--nl-gold-hover)] transition-colors duration-300" data-testid="driver-save">Save & Text Driver</button>
            </div>
          </form>
        </div>
      )}

      {notifyModal && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setNotifyModal(null)} data-testid="notify-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md border border-white/10 bg-[var(--nl-bg-2)] p-8 space-y-5">
            <div>
              <p className="label text-gold mb-1">Notify Rider</p>
              <h3 className="font-serif font-light text-2xl">{notifyModal.name}</h3>
              <p className="text-white/40 text-sm font-body mt-1">{notifyModal.pickup} → {notifyModal.dropoff}</p>
            </div>
            <div>
              <label className="label text-white/50 block mb-2">Message (push + SMS)</label>
              <textarea
                value={notifyText}
                onChange={(e) => setNotifyText(e.target.value)}
                rows={3}
                className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold resize-none"
                data-testid="notify-message"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {NOTIFY_PRESETS.map((p, i) => (
                <button key={i} type="button" onClick={() => setNotifyText(p)} className="label text-[10px] text-white/60 border border-white/15 px-2.5 py-1 hover:text-gold hover:border-gold transition-colors duration-300" data-testid={`notify-preset-${i}`}>
                  {["Outside", "Arrived", "Running late"][i]}
                </button>
              ))}
            </div>
            <p className="text-white/35 text-xs font-body">Sends a push to any device the rider installed the app on, plus a text message.</p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setNotifyModal(null)} className="flex-1 label border border-white/15 text-white/60 py-3 hover:text-white transition-colors duration-300" data-testid="notify-cancel">Cancel</button>
              <button type="button" onClick={sendNotify} className="flex-1 label bg-gold text-black py-3 hover:bg-[var(--nl-gold-hover)] transition-colors duration-300" data-testid="notify-send">Send Alert</button>
            </div>
          </div>
        </div>
      )}

      {replyModal && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setReplyModal(null)} data-testid="quote-reply-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg border border-white/10 bg-[var(--nl-bg-2)] p-8 space-y-5 max-h-[90vh] overflow-y-auto">
            <div>
              <p className="label text-gold mb-1">Reply to quote</p>
              <h3 className="font-serif font-light text-2xl">{replyModal.name}</h3>
              <p className="text-white/40 text-sm font-body mt-1">{replyModal.email}{replyModal.phone ? ` · ${replyModal.phone}` : ""}</p>
              <p className="text-white/55 text-sm font-body mt-3 border-l-2 border-white/15 pl-3">{replyModal.message}</p>
            </div>
            <div>
              <label className="label text-white/50 block mb-2 text-xs">Your price — CAD, pre-tax (optional)</label>
              <input type="number" min="0" step="1" value={replyPrice} onChange={(e) => setReplyPrice(e.target.value)} placeholder="e.g. 220" className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="quote-reply-price" />
            </div>
            <div>
              <label className="label text-white/50 block mb-2 text-xs">Message to client</label>
              <textarea value={replyMsg} onChange={(e) => setReplyMsg(e.target.value)} rows={4} className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold resize-none" data-testid="quote-reply-message" />
            </div>

            <div className="border border-white/10 p-4">
              <label className="flex items-center gap-3 cursor-pointer" data-testid="quote-paylink-toggle">
                <input type="checkbox" checked={payLink} onChange={(e) => setPayLink(e.target.checked)} className="accent-[var(--nl-gold)] w-4 h-4" />
                <span className="font-body text-white text-sm font-semibold">Include a “Reserve &amp; pay” link (locks this price)</span>
              </label>
              {payLink && (
                <div className="grid grid-cols-2 gap-3 mt-4" data-testid="quote-trip-fields">
                  <input value={replyTrip.pickup} onChange={(e) => setReplyTrip({ ...replyTrip, pickup: e.target.value })} placeholder="Pickup address" className="col-span-2 bg-[var(--nl-bg-3)] border border-white/10 px-3 py-2 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="quote-trip-pickup" />
                  <input value={replyTrip.dropoff} onChange={(e) => setReplyTrip({ ...replyTrip, dropoff: e.target.value })} placeholder="Drop-off address" className="col-span-2 bg-[var(--nl-bg-3)] border border-white/10 px-3 py-2 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="quote-trip-dropoff" />
                  <input type="date" value={replyTrip.date} onChange={(e) => setReplyTrip({ ...replyTrip, date: e.target.value })} className="bg-[var(--nl-bg-3)] border border-white/10 px-3 py-2 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="quote-trip-date" />
                  <input type="time" value={replyTrip.time} onChange={(e) => setReplyTrip({ ...replyTrip, time: e.target.value })} className="bg-[var(--nl-bg-3)] border border-white/10 px-3 py-2 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="quote-trip-time" />
                  <select value={replyTrip.vehicle} onChange={(e) => setReplyTrip({ ...replyTrip, vehicle: e.target.value })} className="bg-[var(--nl-bg-3)] border border-white/10 px-3 py-2 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="quote-trip-vehicle">
                    {["Cadillac XTS", "Mercedes S-Class", "Chevrolet Suburban", "GMC Yukon Elevation"].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                  <input type="number" min="1" value={replyTrip.passengers} onChange={(e) => setReplyTrip({ ...replyTrip, passengers: e.target.value })} placeholder="Passengers" className="bg-[var(--nl-bg-3)] border border-white/10 px-3 py-2 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="quote-trip-pax" />
                  <p className="col-span-2 text-white/35 text-[11px] font-body">HST is added at checkout. The client pays exactly ${replyPrice ? (parseFloat(replyPrice) * 1.13).toFixed(2) : "0.00"} (incl. HST) as a card hold.</p>
                </div>
              )}
            </div>

            <p className="text-white/35 text-xs font-body">Sends by email{replyModal.phone ? " and text" : ""}{payLink ? " with a secure pay link that expires in 24 hours" : ", with a link to book"}. Saved &amp; marked “quoted”.</p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setReplyModal(null)} className="flex-1 label border border-white/15 text-white/60 py-3 hover:text-white transition-colors duration-300" data-testid="quote-reply-dismiss">Cancel</button>
              <button type="button" onClick={sendReply} disabled={replySending} className="flex-1 label bg-gold text-black py-3 hover:bg-[var(--nl-gold-hover)] transition-colors duration-300 disabled:opacity-50" data-testid="quote-reply-submit">{replySending ? "Sending…" : "Send quote"}</button>
            </div>
          </div>
        </div>
      )}

      {cancelModal && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setCancelModal(null)} data-testid="admin-cancel-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md border border-white/10 bg-[var(--nl-bg-2)] p-8 space-y-5">
            <div>
              <p className="label text-red-400 mb-1">Cancel Ride</p>
              <h3 className="font-serif font-light text-2xl">{cancelModal.name}</h3>
              <p className="text-white/40 text-sm font-body mt-1">{cancelModal.date} {cancelModal.time} · {cancelModal.pickup} → {cancelModal.dropoff}</p>
            </div>
            <div>
              <label className="label text-white/50 block mb-2">Reason for cancellation</label>
              <select value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className="w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold" data-testid="admin-cancel-reason">
                <option value="">Choose a reason…</option>
                {staffReasons.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <p className="text-white/35 text-xs font-body">A no-show charges the client 100%. Any other reason cancels free of charge and auto-releases the card hold. The client is texted &amp; push-notified.</p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setCancelModal(null)} className="flex-1 label border border-white/15 text-white/60 py-3 hover:text-white transition-colors duration-300" data-testid="admin-cancel-dismiss">Keep ride</button>
              <button type="button" onClick={doAdminCancel} className="flex-1 label bg-red-500 text-white py-3 hover:bg-red-600 transition-colors duration-300" data-testid="admin-cancel-confirm">Cancel ride</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
